import { useTheme } from 'next-themes';
import { useSyncExternalStore } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsRecordingRenderer } from '@/pages/meeting/hooks/useIsRecordingRenderer';
import { router } from '@/router';
import { TOAST_DURATION_MS } from '@/utils/toastTimer';

// Mobile in-call UI: the control bar owns the bottom of the screen and the
// meeting header (with the settings trigger) owns the top, so toasts anchor
// top-center but offset below the header row, and drop below the overlay layer
// (dialogs/drawers are z-50) so an open Media Settings panel stays on top of
// them. Desktop keeps the stock bottom-right toaster everywhere. The Toaster
// mounts outside RouterProvider, hence the direct router subscription instead
// of useLocation.
const MEETING_ROUTE_PREFIXES = ['/join/', '/practice-call'];

const isMeetingPathname = (pathname: string): boolean =>
	MEETING_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const PRACTICE_ROUTE_PREFIX = '/practice-call';

const usePathnameMatch = (matches: (pathname: string) => boolean) =>
	useSyncExternalStore(
		(onChange) => router.subscribe('onResolved', onChange),
		() => matches(router.state.location.pathname),
	);

const isPracticePathname = (pathname: string): boolean => pathname.startsWith(PRACTICE_ROUTE_PREFIX);

const useIsMeetingRoute = () => usePathnameMatch(isMeetingPathname);

// Clears the mobile meeting header row so toasts appear below it, not over it.
const MOBILE_MEETING_TOP_OFFSET = 72;

// Desktop in-call toasts sit above the control bar (h-16 + gap-3) and line up with the tile's md:px-4 edge.
const DESKTOP_MEETING_OFFSET = { bottom: 88, right: 16 };

type ToasterOffsetOptions = {
	belowHeader: boolean;
	inDesktopMeeting: boolean;
};

const toasterOffset = ({ belowHeader, inDesktopMeeting }: ToasterOffsetOptions) => {
	if (belowHeader) return { top: MOBILE_MEETING_TOP_OFFSET };
	if (inDesktopMeeting) return DESKTOP_MEETING_OFFSET;
	return undefined;
};

const Toaster = ({ ...props }: ToasterProps) => {
	const { theme = 'system' } = useTheme();
	const isRecordingRenderer = useIsRecordingRenderer();
	const isMeetingRoute = useIsMeetingRoute();
	const isPracticeRoute = usePathnameMatch(isPracticePathname);
	const isMobile = useIsMobile();
	const inMobileMeeting = isMeetingRoute && isMobile;
	const belowHeader = inMobileMeeting || isPracticeRoute;
	const belowOverlays = inMobileMeeting && !isPracticeRoute;
	const offset = toasterOffset({ belowHeader, inDesktopMeeting: isMeetingRoute && !isMobile });

	// The /record egress renderer must never paint toasts into the recording.
	if (isRecordingRenderer) return null;

	return (
		<Sonner
			theme={isPracticeRoute ? 'dark' : (theme as ToasterProps['theme'])}
			position={belowHeader ? 'top-center' : 'bottom-right'}
			offset={offset}
			mobileOffset={belowHeader ? { top: MOBILE_MEETING_TOP_OFFSET } : undefined}
			duration={TOAST_DURATION_MS}
			className="toaster group"
			icons={{
				success: <CircleCheckIcon className="size-4" />,
				info: <InfoIcon className="size-4" />,
				warning: <TriangleAlertIcon className="size-4" />,
				error: <OctagonXIcon className="size-4" />,
				loading: <Loader2Icon className="size-4 animate-spin" />,
			}}
			style={
				{
					'--normal-bg': 'var(--popover)',
					'--normal-text': 'var(--popover-foreground)',
					'--normal-border': 'var(--border)',
					'--border-radius': 'var(--radius)',
					'--toast-duration': `${TOAST_DURATION_MS}ms`,
					...(belowOverlays && { zIndex: 40 }),
				} as React.CSSProperties
			}
			toastOptions={{
				classNames: {
					toast: 'cn-toast',
				},
			}}
			{...props}
		/>
	);
};

export { Toaster };
